/**
 * A two-step link for roadmap maps: the first press on a link item opens a
 * small callout beside it, and a second press (or the callout's button)
 * follows the link. The item acts as a disclosure button, so screen readers
 * announce it as expanded or collapsed, and the callout follows it in the
 * DOM so Tab reaches its button next.
 */
import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type MouseEvent,
} from 'react';
import styled from 'styled-components';

/** Width of the callout in the map's logical pixels. */
const CALLOUT_WIDTH = 260;
/** Space kept between the callout and the map's side edges. */
const EDGE_MARGIN = 8;

/**
 * The DOM id of a roadmap item's callout, for the trigger's `aria-controls`.
 *
 * @param itemId - The roadmap item's id.
 * @return An id that is valid in HTML and unique per item.
 * @throws Never.
 */
export function roadmapLinkCalloutId(itemId: string): string {
  return `roadmap-link-callout-${itemId.replace(/[^A-Za-z0-9_-]/g, '_')}`;
}

/**
 * The in-app path a roadmap link opens, so it routes in the same tab and the
 * browser's Back returns to the roadmap. An absolute http(s) URL on this
 * site (a copied share link, say) becomes its path, query and hash.
 *
 * @param href - The link.
 * @param options.origin - This site's origin. Omitted: the page's.
 * @return The path, or null for a link to another site.
 * @throws Never.
 */
export function roadmapInAppPath(
  href: string,
  {origin = window.location.origin}: {origin?: string} = {},
): string | null {
  if (!/^https?:\/\//i.test(href)) {
    return href;
  }
  try {
    const url = new URL(href);
    return url.origin === origin ? `${url.pathname}${url.search}${url.hash}` : null;
  } catch (e) {
    console.debug('[RoadmapLinkCallout] unparsable link', href, e);
    return null;
  }
}

/**
 * Whether a roadmap link leaves the app, and so opens in a new tab.
 *
 * @param href - The link.
 * @return True for absolute http(s) URLs on another site.
 * @throws Never.
 */
export function isExternalRoadmapHref(href: string): boolean {
  return roadmapInAppPath(href) === null;
}

/**
 * The callout's button text, "Open" plus the item's title with a
 * sentence-case first word lowered ("Course textbook" → "Open course
 * textbook"). A first word in capitals, like an acronym, is kept.
 *
 * @param title - The item's title.
 * @return The button text.
 * @throws Never.
 */
export function roadmapLinkActionLabel(title: string): string {
  const trimmed = title.trim();
  const isSentenceCase = /^[A-Z][a-z]/.test(trimmed);
  const lowered = isSentenceCase
    ? trimmed.charAt(0).toLowerCase() + trimmed.slice(1)
    : trimmed;
  return lowered === '' ? 'Open' : `Open ${lowered}`;
}

/** What {@link useRoadmapLinkCallout} hands a map. */
export interface RoadmapLinkCalloutState {
  /** The item whose callout is open, or undefined when none is. */
  openId: string | undefined;
  /**
   * Handles a press on a link item: opens its callout on the first press and
   * calls `follow` (then closes) on a press while its callout is open.
   */
  press: (args: {
    id: string;
    trigger: HTMLElement | null;
    follow: () => void;
  }) => void;
  /**
   * Closes the open callout. With `restoresFocus`, focus goes back to the
   * item that opened it; otherwise focus is left where it is.
   */
  close: (args?: {restoresFocus?: boolean}) => void;
}

/**
 * Tracks which link item's callout is open on a map. One callout is open at
 * a time; pressing a different link item moves it there.
 *
 * @return The open item and the press and close handlers.
 * @throws Never.
 */
export function useRoadmapLinkCallout(): RoadmapLinkCalloutState {
  const [openId, setOpenId] = useState<string | undefined>(undefined);
  const triggerRef = useRef<HTMLElement | null>(null);

  const close = useCallback(
    ({restoresFocus = false}: {restoresFocus?: boolean} = {}) => {
      setOpenId(undefined);
      if (restoresFocus) {
        triggerRef.current?.focus();
      }
    },
    [],
  );

  const press = useCallback<RoadmapLinkCalloutState['press']>(
    ({id, trigger, follow}) => {
      if (openId === id) {
        setOpenId(undefined);
        follow();
        return;
      }
      triggerRef.current = trigger;
      setOpenId(id);
    },
    [openId],
  );

  return {openId, press, close};
}

/**
 * The attributes that make a link item's button a disclosure for its
 * callout. Spread them onto the item's `<button>`.
 *
 * @param args.itemId - The roadmap item's id.
 * @param args.isOpen - Whether the item's callout is open.
 * @return `aria-expanded`, plus `aria-controls` while the callout exists.
 * @throws Never.
 */
export function roadmapLinkTriggerProps({
  itemId,
  isOpen,
}: {
  itemId: string;
  isOpen: boolean;
}): {'aria-expanded': boolean; 'aria-controls'?: string} {
  if (!isOpen) {
    return {'aria-expanded': false};
  }
  return {
    'aria-expanded': true,
    'aria-controls': roadmapLinkCalloutId(itemId),
  };
}

/** Props for {@link RoadmapLinkCallout}. */
export interface RoadmapLinkCalloutProps {
  /** The roadmap item's id; names the callout for its trigger. */
  itemId: string;
  /** The item's title, shown as the callout's heading. */
  title: string;
  /** One line about where the link goes. When omitted, none is shown. */
  description?: string;
  /** Where the link goes: an in-app path or an external URL. */
  href: string;
  /** Horizontal centre of the item, in the map's logical pixels. */
  centerX: number;
  /** Top edge of the callout, in the map's logical pixels. */
  top: number;
  /**
   * Width of the map in logical pixels. The callout is shifted to stay
   * inside it, since the maps clip horizontal overflow.
   */
  boundsWidth: number;
  /** Follows the link; called when the callout's button is pressed. */
  onFollow: () => void;
  /** Closes the callout; `restoresFocus` is set for Escape and the close button. */
  onClose: (args: {restoresFocus: boolean}) => void;
}

/**
 * The callout a link item opens on a roadmap map. Escape or the close
 * button closes it and returns focus to the item; a press anywhere outside
 * it and its item closes it without moving focus.
 *
 * @param props - See {@link RoadmapLinkCalloutProps}.
 * @return The positioned callout.
 * @throws Never.
 */
export function RoadmapLinkCallout({
  itemId,
  title,
  description,
  href,
  centerX,
  top,
  boundsWidth,
  onFollow,
  onClose,
}: RoadmapLinkCalloutProps) {
  const headingId = useId();
  const calloutId = roadmapLinkCalloutId(itemId);
  const calloutRef = useRef<HTMLDivElement>(null);
  const isExternal = isExternalRoadmapHref(href);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose({restoresFocus: true});
      }
    };
    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target;
      if (!(target instanceof Node)) {
        return;
      }
      if (calloutRef.current?.contains(target)) {
        return;
      }
      // The item's own press toggles the callout; closing here first would
      // turn the second press into a reopen.
      const trigger = document.querySelector(`[aria-controls="${calloutId}"]`);
      if (trigger?.contains(target)) {
        return;
      }
      onClose({restoresFocus: false});
    };
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('pointerdown', handlePointerDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('pointerdown', handlePointerDown);
    };
  }, [calloutId, onClose]);

  const left = Math.min(
    Math.max(centerX - CALLOUT_WIDTH / 2, EDGE_MARGIN),
    Math.max(EDGE_MARGIN, boundsWidth - CALLOUT_WIDTH - EDGE_MARGIN),
  );

  const handleFollow = (event: MouseEvent<HTMLAnchorElement>) => {
    // Modified clicks keep the browser's own behaviour (new tab, new
    // window); a plain click goes through the map so in-app links route.
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
      return;
    }
    event.preventDefault();
    onFollow();
  };

  return (
    <Callout
      ref={calloutRef}
      id={calloutId}
      role="group"
      aria-labelledby={headingId}
      $left={left}
      $top={top}
      $arrowX={centerX - left}
    >
      <Heading id={headingId}>{title}</Heading>
      {description && <Description>{description}</Description>}
      <FollowLink
        href={href}
        target={isExternal ? '_blank' : undefined}
        rel={isExternal ? 'noopener noreferrer' : undefined}
        onClick={handleFollow}
      >
        {roadmapLinkActionLabel(title)}
        {isExternal && (
          <>
            {' '}
            <span aria-hidden>↗</span>{' '}
            <span className="visually-hidden">(opens in a new tab)</span>
          </>
        )}
      </FollowLink>
      <CloseButton
        type="button"
        aria-label={`Close ${title}`}
        onClick={() => onClose({restoresFocus: true})}
      >
        <span aria-hidden>×</span>
      </CloseButton>
    </Callout>
  );
}

const INK = '#1d2433';

const Callout = styled.div<{$left: number; $top: number; $arrowX: number}>`
  position: absolute;
  left: ${({$left}) => $left}px;
  top: ${({$top}) => $top}px;
  z-index: 5;
  width: ${CALLOUT_WIDTH}px;
  padding: 14px 16px 16px;
  border-radius: 12px;
  background: #ffffff;
  color: ${INK};
  text-align: left;
  box-shadow:
    0 10px 28px rgba(29, 36, 51, 0.18),
    0 0 0 1px rgba(29, 36, 51, 0.12);

  &::before {
    content: '';
    position: absolute;
    top: -6px;
    left: ${({$arrowX}) => $arrowX - 6}px;
    width: 12px;
    height: 12px;
    background: #ffffff;
    box-shadow: -1px -1px 0 rgba(29, 36, 51, 0.12);
    transform: rotate(45deg);
  }
`;

const Heading = styled.p`
  margin: 0 28px 4px 0;
  font-size: 1rem;
  font-weight: 700;
  line-height: 1.3;
`;

const Description = styled.p`
  margin: 0 0 12px;
  font-size: 0.875rem;
  line-height: 1.4;
  color: #3d4555;
`;

const FollowLink = styled.a`
  display: inline-block;
  padding: 8px 14px;
  border-radius: 8px;
  background: ${INK};
  color: #ffffff;
  font-size: 0.9rem;
  font-weight: 600;
  text-decoration: none;

  &:hover,
  &:focus-visible {
    color: #ffffff;
    background: #343d52;
  }

  &:focus-visible {
    outline: 3px solid #3b6fd8;
    outline-offset: 2px;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 8px;
  right: 8px;
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: #3d4555;
  font-size: 1.25rem;
  line-height: 1;

  &:hover {
    background: #eef0f4;
  }

  &:focus-visible {
    outline: 3px solid #3b6fd8;
    outline-offset: 1px;
  }
`;
